/**
 * 路由守卫
 * 
 * 实现：
 * - 认证守卫：未登录用户重定向到登录页
 * - 已登录守卫：已登录用户访问登录页时重定向到工作台
 * - 标题守卫：自动设置页面标题
 */

import type { Router } from 'vue-router'
import { useAuthStore } from '@/stores'

/** 需要认证的路由白名单（不需要登录） */
const PUBLIC_ROUTE_NAMES = ['login']

/** 设置路由守卫 */
export function setupRouterGuards(router: Router): void {
  // 全局前置守卫
  router.beforeEach(async (to, _from, next) => {
    const authStore = useAuthStore()

    // 设置页面标题
    const title = to.meta.title as string | undefined
    if (title) {
      document.title = `${title} - 个人工作台`
    } else {
      document.title = '个人工作台'
    }

    // 检查是否需要认证
    const requiresAuth = !PUBLIC_ROUTE_NAMES.includes(to.name as string)

    if (requiresAuth) {
      // 需要认证但未登录
      if (!authStore.isAuthenticated) {
        // 尝试从会话恢复用户
        const success = await authStore.fetchCurrentUser()
        if (!success) {
          // 未登录，重定向到登录页
          next({ name: 'login', query: { redirect: to.fullPath } })
          return
        }
      }
    } else {
      // 已登录用户访问公开路由（如登录页）
      if (authStore.isAuthenticated && to.name === 'login') {
        next({ name: 'dashboard' })
        return
      }
    }

    next()
  })

  // 全局后置钩子
  router.afterEach((to) => {
    // 可用于埋点、页面切换动画等
    console.debug(`[Router] 导航至: ${to.path}`)
  })
}
